import { Component } from 'react'
import { useTranslation } from 'react-i18next'
import { isChunkLoadError } from '../utils/lazyWithReload'
import './ChunkErrorBoundary.scss'

/**
 * Localized fallback shown when a lazy route fails to load even after the
 * auto-reload safety net has already fired once (#533). Gives the user an
 * explicit Reload button rather than a blank screen.
 */
const ChunkErrorFallback = ({ onReload }) => {
  const { t } = useTranslation('common')
  return (
    <div className="chunk-error" role="alert">
      <h2 className="chunk-error__title">{t('chunkError.title')}</h2>
      <p className="chunk-error__message">{t('chunkError.message')}</p>
      <button type="button" className="chunk-error__reload" onClick={onReload}>
        {t('chunkError.reload')}
      </button>
    </div>
  )
}

/**
 * Error boundary around the lazy route tree. A rejected dynamic import (stale
 * chunk after a deploy) would otherwise unmount the whole app and white-screen
 * it. This catches that error and renders a manual Reload prompt. For non-chunk
 * errors it rethrows so higher-level handling / normal error reporting applies.
 */
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      // eslint-disable-next-line no-console
      console.warn('Chunk load failed (likely a stale deploy):', error?.message)
    }
  }

  render() {
    if (this.state.hasError) {
      if (isChunkLoadError(this.state.error)) {
        return <ChunkErrorFallback onReload={() => window.location.reload()} />
      }
      // Not a stale-chunk error — rethrow so it bubbles to any ancestor
      // boundary / normal error handling instead of being swallowed here.
      throw this.state.error
    }
    return this.props.children
  }
}

export default ChunkErrorBoundary
